import { ADMIN_PERMISSION_KEYS } from "@/routing/adminPermissionKeys";
import type {
    AdminV2RevenueMenuVisibility,
    AdminV2RevenueMenuVisibilityKey,
} from "@/types/adminV2";

type ErrorRecord = Record<string, unknown>;

const isErrorRecord = (value: unknown): value is ErrorRecord =>
    Boolean(value) && typeof value === "object";

const getStringValue = (
    record: ErrorRecord,
    key: string,
): string | undefined => {
    const value = record[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
};

const getErrorDetailMessage = (record: ErrorRecord): string | undefined => {
    const data = record.data;
    if (!isErrorRecord(data)) {
        return undefined;
    }

    const detail = data.detail;
    return typeof detail === "string" && detail.length > 0 ? detail : undefined;
};

export const formatApiError = (
    error: unknown,
): { message: string; correlation?: string } => {
    if (!isErrorRecord(error)) {
        return { message: "Unknown error resolving access controls." };
    }

    const correlation = getStringValue(error, "correlationId");
    const message =
        getStringValue(error, "message") ??
        getErrorDetailMessage(error) ??
        "Unable to resolve route access controls.";

    return { message, correlation };
};

export const resolveBypassPermissionKeys = (
    configuredPermissionKeys: string[] | null,
): Set<string> =>
    new Set(configuredPermissionKeys ?? [...ADMIN_PERMISSION_KEYS]);

export const resolveBypassMenuVisibility = (
    configuredMenuVisibility:
        | AdminV2RevenueMenuVisibility
        | null
        | undefined,
): AdminV2RevenueMenuVisibility =>
    configuredMenuVisibility ?? {
        revenue: true,
        learning: true,
        sales: true,
    };

export const shouldCheckPermission = (
    requiredPermission: string | undefined,
    adminV2FoundationsEnabled: boolean,
    adminE2eBypassEnabled: boolean,
): boolean =>
    Boolean(requiredPermission) &&
    adminV2FoundationsEnabled &&
    !adminE2eBypassEnabled;

export const shouldCheckMenuVisibility = (
    requiredMenuVisibility: AdminV2RevenueMenuVisibilityKey | undefined,
    adminV2FoundationsEnabled: boolean,
    adminE2eBypassEnabled: boolean,
): boolean =>
    Boolean(requiredMenuVisibility) &&
    adminV2FoundationsEnabled &&
    !adminE2eBypassEnabled;
